import { useState } from "react";
import {
  saveTemplateApi,
  deleteTemplateApi,
} from "../services/template-service";
import { useNotification } from "../../../context/notification-provider";
import type { TemplateApiItem } from "../types/api-types";

interface UseTemplateApisManagementProps {
  templateId: number;
  mutate: () => void;
}

const useTemplateApisManagement = ({
  templateId,
  mutate,
}: UseTemplateApisManagementProps) => {
  const { showNotification } = useNotification();

  // Add state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>("");
  const [selectedApiId, setSelectedApiId] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [apiToDelete, setApiToDelete] = useState<TemplateApiItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setSelectedEndpoint("");
    setSelectedApiId(0);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEndpoint("");
    setSelectedApiId(0);
  };

  const handleSave = async () => {
    if (!selectedEndpoint || selectedApiId === 0) return;

    setIsSaving(true);
    try {
      await saveTemplateApi({
        id: 0,
        templateId,
        apiId: selectedApiId,
        endpoint: selectedEndpoint,
      });
      showNotification({
        message: "API added successfully",
        severity: "success",
      });
      mutate();
      handleCloseDialog();
    } catch (err) {
      showNotification({
        message: `Failed to add API. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (api: TemplateApiItem) => {
    setApiToDelete(api);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!apiToDelete) return;

    setIsDeleting(true);
    try {
      await deleteTemplateApi(apiToDelete);
      mutate();
      showNotification({
        message: "API deleted successfully",
        severity: "success",
      });
      setDeleteConfirmOpen(false);
      setApiToDelete(null);
    } catch (err) {
      showNotification({
        message: `Failed to delete API. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setApiToDelete(null);
  };

  return {
    // Add dialog state
    openDialog,
    selectedEndpoint,
    selectedApiId,
    isSaving,
    setSelectedEndpoint,
    setSelectedApiId,
    handleOpenDialog,
    handleCloseDialog,
    handleSave,

    // Delete dialog state
    deleteConfirmOpen,
    apiToDelete,
    isDeleting,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};

export default useTemplateApisManagement;
